import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import NavRoutes from "./routes/NavRoutes";
import PageWrapper from "./components/PageWrapper/PageWrapper";
import Loader from "./components/Loader/Loader";
import useApp from "./utils/hooks/useApp";
import { clss } from "./utils/helpers/styleHelpers";
import {
  AUTH_STORAGE_SYNC_KEY,
  getAllUsersFromStorage,
  getCurrentUserProfile,
  getGuestLangFromStorage,
  getLoggedUser,
  repairTokenStorageIfNeeded,
} from "./utils/helpers/tokenStorage";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { login, setLoggedUsers } from "./store/slices/authSlice";
import { CultureCodeE, DirectionE, LangE, TourGateStatusE } from "./models/enums";
import { useLazyGetUpdatedTranslationsQuery } from "./api/translationsAPI";
import { selectCurrentLang } from "./store/selectors/appSelectors";
import { useLazyGetUserByIdQuery } from "./api/userAPI";
import { ToastContainer } from "react-toastify";
import ProductTourWrapper from "./components/ProductTour/ProductTourWrapper";
import { selectCurrentUser } from "./store/selectors/authSelectors";
import GoogleAnalyticsHandler from "./components/GoogleAnalyticsHandler/GoogleAnalyticsHandler";
import TagManager from "react-gtm-module";
import useGlobalNotification from "./utils/hooks/useGlobalNotification";
import { loadChatWidget, updateHtmlLangAttribute } from "./utils/helpers/chatWidget";
import { isTourEligibleUser } from "./components/ProductTour/helpers/isTourEligibleUser";
import { setTourGateStatus } from "./store/slices/productTourSlice";

import styles from "./utils/styles/appStyles.module.scss";

const _UNSAFE_GA_ID_BETA = "GTM-WH4WWXD";

const App = () => {
  const {
    direction, // define ltr or rtl direction className for the whole app
    blurredBackground, // get blurred background className if popup window is opened
    switchAppLang,
  } = useApp();
  const dispatch = useAppDispatch();
  const lang = useAppSelector(selectCurrentLang);
  const user = useAppSelector(selectCurrentUser);
  const [_, { isLoading: isLoadingTranslations }] =
    useLazyGetUpdatedTranslationsQuery();
  const [getUserData] = useLazyGetUserByIdQuery();
  const [isDataFetched, setIsDataFetched] = useState(false);
  const isBetaVersion = window.location.href.includes("beta-media");
  const widgetInitializedRef = useRef(false);

  const hasTour = isTourEligibleUser(user);

  useGlobalNotification();

  useEffect(() => {
    // <ProductTourWrapper/> owns the tour gate while it is mounted, but it only mounts for the users
    // that get a tour. For everyone else (global admins included) nothing would ever release the gate
    // and the global notification popup would wait forever — so release it here as soon as we know
    // who the user is.
    if (user && !hasTour) {
      dispatch(setTourGateStatus(TourGateStatusE.Cleared));
    }
  }, [user, hasTour, dispatch]);

  useEffect(() => {
    // add google tag manager if it is prod
    if (isBetaVersion) {
      const tagManagerArgs = {
        gtmId: _UNSAFE_GA_ID_BETA,
      };

      TagManager.initialize(tagManagerArgs);
    }
  }, []);

  const handleResize = useCallback(() => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }, []);

  const checkIsUserChanged = useCallback(
    (event: StorageEvent) => {
      if (
        event.key !== null &&
        event.key !== "tokens" &&
        event.key !== AUTH_STORAGE_SYNC_KEY
      ) {
        return;
      }

      const storedUserId = getCurrentUserProfile()?.userId;

      if (!storedUserId && user?.userId) {
        window.location.reload();
        return;
      }

      if (storedUserId && storedUserId !== user?.userId) {
        window.location.reload();
      }
    },
    [user?.userId]
  );

  useEffect(() => {
    repairTokenStorageIfNeeded();

    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);

    window.addEventListener("resize", handleResize);
    window.addEventListener("storage", checkIsUserChanged);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("storage", checkIsUserChanged);
    };
  }, [checkIsUserChanged, handleResize]);

  useEffect(() => {
    const user = getLoggedUser()?.user;
    const allUsers = getAllUsersFromStorage();

    if (allUsers.length) {
      dispatch(setLoggedUsers(allUsers));
    }

    if (user) {
      getUserData({ Id: user?.user?.userId }).then((res) => {
        dispatch(
          login({
            ...user,
            user: {
              ...user?.user,
              culture: res.data?.Culture,
            },
          })
        );
        // More robust language detection - check for both "en-US" and "he-IL" explicitly
        const culture = res.data?.Culture?.toLowerCase() || "";
        let currentLang: LangE;
        if (culture === "en-us" || culture.startsWith("en")) {
          currentLang = LangE.EN;
        } else if (culture === "he-il" || culture.startsWith("he")) {
          currentLang = LangE.HE;
        } else {
          // Fallback to default
          console.warn("[App] Unknown culture code, defaulting to EN. Culture was:", res.data?.Culture);
          currentLang = LangE.EN;
        }
        updateHtmlLangAttribute(currentLang);
        switchAppLang(currentLang).then(() => {
          setIsDataFetched(true);
        });
      });
    } else {
      const guestCulture = getGuestLangFromStorage();
      const initialLang =
        guestCulture === CultureCodeE.HE ? LangE.HE : LangE.EN;
      updateHtmlLangAttribute(initialLang);
      switchAppLang(initialLang).then(() => {
        setIsDataFetched(true);
      });
    }
  }, []);

  // Initialize chat widget once after data is fetched and language is set
  // Note: Language changes are handled by LangSelect component, not here
  useEffect(() => {
    // Only initialize once when data is fetched and lang is available
    // The ref ensures we don't initialize multiple times even if useEffect runs again
    if (isDataFetched && lang && !widgetInitializedRef.current) {
      updateHtmlLangAttribute(lang);
      widgetInitializedRef.current = true;
      loadChatWidget(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetched]); // Only depend on isDataFetched - lang is checked inside but we don't want to re-run when lang changes

  return (
    <>
      {user && !isLoadingTranslations && hasTour && (
        <ProductTourWrapper tourName="campaigns" withSplashScreen />
      )}
      <ToastContainer
        rtl={direction === DirectionE.RTL}
        position={direction === DirectionE.LTR ? "top-right" : "top-left"}
        className={styles.toastContainer}
      />
      <div className={clss([direction, blurredBackground])}>
        <GoogleAnalyticsHandler />
        <PageWrapper>
          {isDataFetched && (
            <Suspense fallback={<Loader />}>
              <NavRoutes />
            </Suspense>
          )}
        </PageWrapper>
      </div>
    </>
  );
};

export default App;
